/**
 * WelcomeCreditCard
 *
 * Shown on the auth success screen for brand-new accounts right after the
 * A$1 signup bonus is booked. Figures are REAL:
 *  - balance is read from `useCallingBalance` (calling_credit table)
 *  - minutes-per-country are computed from `useCountryRates` (live edge
 *    function output, same source the dialer + rates page use)
 *
 * No hardcoded "10-15 minutes" copy. If a rate moves, this card moves with it.
 */
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, ArrowRight, Sparkles, CreditCard, UserCheck } from "lucide-react";
import { useCallingBalance } from "@/hooks/useCallingBalance";
import { useCountryRates } from "@/hooks/useCountryRates";
import { TermsDialog } from "@/components/connect/TermsDialog";
import { PrivacyDialog } from "@/components/connect/PrivacyDialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface Props {
  firstName?: string | null;
  className?: string;
}

// Countries to surface as "how far A$1 goes" examples. Order is a hint only;
// we re-rank by actual minutes yield so the top examples always show the
// most generous real value.
const SHOWCASE_COUNTRIES = ["254", "251", "256", "255", "250", "211", "252", "1", "44", "91"];

export function WelcomeCreditCard({ firstName, className }: Props) {
  const { balance, isLoading: balanceLoading } = useCallingBalance();
  const { all, currencySymbol, currency, isLoading: ratesLoading } = useCountryRates();

  const available = balance?.availableCents ?? 0;
  const displayCurrency = balance?.currency || currency || "AUD";
  const symbol = displayCurrency === "AUD" ? "A$" : currencySymbol || "$";
  const amount = (available / 100).toFixed(2);

  const examples = useMemo(() => {
    if (!all?.length || available <= 0) return [];
    const seen = new Set<string>();
    const rows = SHOWCASE_COUNTRIES
      .map((cc) => {
        // Prefer the cheapest active rate per country (mobile or fixed).
        const matches = all.filter((r) => r.countryCode === cc && r.ratePerMinuteCents > 0);
        if (!matches.length) return null;
        const cheapest = matches.reduce((a, b) =>
          a.ratePerMinuteCents <= b.ratePerMinuteCents ? a : b,
        );
        return cheapest;
      })
      .filter((r): r is NonNullable<typeof r> => !!r)
      .filter((r) => {
        if (seen.has(r.countryCode)) return false;
        seen.add(r.countryCode);
        return true;
      })
      .map((r) => ({
        id: r.id,
        name: r.countryName,
        flag: r.flag,
        minutes: Math.floor(available / r.ratePerMinuteCents),
      }))
      .filter((r) => r.minutes >= 1)
      .sort((a, b) => b.minutes - a.minutes)
      .slice(0, 3);
    return rows;
  }, [all, available]);

  const loading = balanceLoading || ratesLoading;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: "easeOut" }}
      className={cn("w-full max-w-md mx-auto", className)}
    >
      <div className="relative rounded-[2rem] border border-border/40 bg-card shadow-card overflow-hidden">
        {/* Header band */}
        <div className="bg-[linear-gradient(135deg,hsl(var(--elefa-gold)/0.22),hsl(var(--primary)/0.16))] px-6 pt-6 pb-5">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 shadow-sm">
            <Sparkles className="h-3 w-3" />
            Welcome gift
          </div>
          <h2 className="mt-3 text-2xl font-bold text-foreground leading-tight">
            You're in{firstName ? `, ${firstName}` : ""}.
          </h2>
          <p className="text-sm text-foreground/70 mt-1">
            Your account is ready. We've added a small credit so you can call
            home right now.
          </p>
        </div>

        {/* Amount */}
        <div className="px-6 pt-5 pb-2 text-center">
          <div className="text-5xl font-bold tracking-tight text-foreground tabular-nums">
            {symbol}
            {amount}
          </div>
          <div className="text-xs font-semibold uppercase tracking-wider text-primary mt-1">
            Free calling credit
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            One-time welcome gift, already in your balance.
          </p>
        </div>

        {/* Real-minutes table */}
        <div className="px-6 pt-4">
          <p className="text-xs font-semibold text-foreground/80">How far does it go?</p>
          {loading ? (
            <div className="mt-2 space-y-2">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-8 rounded-md bg-muted/50 animate-pulse" />
              ))}
            </div>
          ) : examples.length === 0 ? (
            <p className="text-xs text-muted-foreground mt-2">
              Pick any country in the dialer to see live per-minute pricing.
            </p>
          ) : (
            <ul className="mt-2 divide-y divide-border/40 rounded-xl border border-border/40 bg-background/60">
              {examples.map((row) => (
                <li
                  key={row.id}
                  className="flex items-center justify-between px-3 py-2 text-sm"
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="text-base leading-none">{row.flag}</span>
                    <span className="truncate text-foreground">{row.name}</span>
                  </span>
                  <span className="font-semibold tabular-nums text-foreground">
                    ~{row.minutes} min
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="text-[11px] text-muted-foreground mt-2">
            Live retail rates. Actual minutes vary by destination and billing
            increment.
          </p>
        </div>

        {/* Trust badges */}
        <div className="px-6 mt-4 grid grid-cols-3 gap-2">
          <Badge icon={<UserCheck className="h-3.5 w-3.5" />} label="One per account" />
          <Badge icon={<ShieldCheck className="h-3.5 w-3.5" />} label="Phone verified" />
          <Badge icon={<CreditCard className="h-3.5 w-3.5" />} label="No card needed" />
        </div>

        {/* CTAs */}
        <div className="px-6 pt-5 pb-5 space-y-2">
          <Button asChild size="lg" className="w-full font-semibold">
            <Link to="/connect-portal/rates">
              See current rates
              <ArrowRight className="h-4 w-4 ml-2" />
            </Link>
          </Button>
          <Button asChild variant="ghost" size="sm" className="w-full text-foreground/70">
            <Link to="/connect-portal/rates">See rates before I call</Link>
          </Button>
        </div>

        {/* Privacy / terms footer */}
        <div className="px-6 pb-5 text-center text-[11px] text-muted-foreground leading-relaxed">
          By using your welcome credit you accept our{" "}
          <TermsDialog
            trigger={
              <span className="text-primary hover:underline cursor-pointer">
                Terms
              </span>
            }
          />{" "}
          and{" "}
          <PrivacyDialog
            trigger={
              <span className="text-primary hover:underline cursor-pointer">
                Privacy Policy
              </span>
            }
          />
          . One credit per verified phone &amp; device. Abuse voids the gift.
        </div>
      </div>
    </motion.div>
  );
}

function Badge({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="flex flex-col items-center gap-1 rounded-xl border border-border/40 bg-background/60 px-2 py-2 text-center">
      <span className="text-primary">{icon}</span>
      <span className="text-[10px] font-medium text-foreground/75 leading-tight">
        {label}
      </span>
    </div>
  );
}
